import React, { useEffect, useState } from 'react';
import { Pressable, View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles, radii } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import type { OrderWidgetProps } from './types';
import { ProjectPipeline, type PipelineProject } from './ProjectPipeline';

export function TkneWidget({ userId, gear, data, onOpen }: OrderWidgetProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const [projects, setProjects] = useState<PipelineProject[]>([]);
  const [loading, setLoading] = useState(true);

  // Load active projects with their stages
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadProjects = async () => {
      try {
        const { data: result, error } = await supabase
          .from('designs')
          .select('id, project_title, sector, is_active, basic_stages')
          .eq('user_id', userId)
          .eq('is_active', true)
          .order('updated_at', { ascending: false })
          .limit(10);

        if (!error && result) {
          const typed = result as PipelineProject[];
          setProjects(typed);
        }
      } finally {
        setLoading(false);
      }
    };

    void loadProjects();
  }, [userId]);

  if (loading) {
    return (
      <GlassCard style={styles.container}>
        <Pressable onPress={onOpen} style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={t.gold} />
        </Pressable>
      </GlassCard>
    );
  }

  if (projects.length === 0) {
    return (
      <GlassCard style={styles.container}>
        <Pressable onPress={onOpen}>
          <Text style={styles.emptyText}>No orders yet</Text>
        </Pressable>
      </GlassCard>
    );
  }

  // Show only the first active project. ProjectPipeline supplies its own
  // card chrome (raised background, padding), so it's rendered unwrapped.
  const activeProject = projects[0];
  return <ProjectPipeline project={activeProject} onOpen={onOpen} />;
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  container: {
    borderRadius: radii.panel,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  loadingContainer: {
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: t.ivorySoft,
    fontStyle: 'italic',
    textAlign: 'center',
    marginVertical: 24,
  },
}),
);
